import { ref } from 'vue'
import { httpService } from '../services/httpService'
import { config } from '../services/configService'
import { eventBus } from '../eventBus'
import { log } from '../utils/DebugLogger'

import type { Traducoes_IdiomaRow } from '../contracts/Traducoes_IdiomaRow'
import type { Traducoes_PaginaRow } from '../contracts/Traducoes_PaginaRow'
import type { Traducoes_ItemRow } from '../contracts/Traducoes_ItemRow'
import type { Traducoes_IdiomasResponseDados } from '../contracts/Traducoes_IdiomasResponse'
import type { Traducoes_PaginasResponseDados } from '../contracts/Traducoes_PaginasResponse'
import type { Traducoes_ListaResponseDados } from '../contracts/Traducoes_ListaResponse'
import type { Traducoes_BuscaResponseDados } from '../contracts/Traducoes_BuscaResponse'
import type { Traducoes_ChaveResponseDados } from '../contracts/Traducoes_ChaveResponse'
import type { Traducoes_CriarChaveResponseDados } from '../contracts/Traducoes_CriarChaveResponse'
import type { Traducoes_RetraduzirResponseDados } from '../contracts/Traducoes_RetraduzirResponse'
import type { Traducoes_SalvarInput } from '../contracts/Traducoes_SalvarInput'
import type { Traducoes_CriarChaveInput } from '../contracts/Traducoes_CriarChaveInput'
import type { Traducoes_RetraduzirInput } from '../contracts/Traducoes_RetraduzirInput'

// Import side-effects: registers the contracts with the validate() registries.
import '../contracts/Traducoes_IdiomaRow'
import '../contracts/Traducoes_PaginaRow'
import '../contracts/Traducoes_ItemRow'
import '../contracts/Traducoes_IdiomasResponse'
import '../contracts/Traducoes_PaginasResponse'
import '../contracts/Traducoes_ListaInput'
import '../contracts/Traducoes_ListaResponse'
import '../contracts/Traducoes_BuscaInput'
import '../contracts/Traducoes_BuscaResponse'
import '../contracts/Traducoes_ChaveInput'
import '../contracts/Traducoes_ChaveResponse'
import '../contracts/Traducoes_CriarChaveInput'
import '../contracts/Traducoes_CriarChaveResponse'
import '../contracts/Traducoes_SalvarInput'
import '../contracts/Traducoes_RetraduzirInput'
import '../contracts/Traducoes_RetraduzirResponse'

/**
 * useTranslationsAdmin — SOLE network owner of the `/config-traducoes` admin
 * panel, speaking the AUTHENTICATED app-api proxy `config/traducoes/*`
 * (controllers/config/traducoes.php, groups Full + Traducoes).
 *
 * IT NO LONGER TALKS TO THE GO SERVICE. The panel used to call
 * translations-service directly on `config.api.idiomasHost`, an API with no
 * authentication at all. The runtime i18n loader
 * (`src/services/translationsService.ts`) still uses that host for the
 * read-only `clients/*` routes — that is deliberate and out of scope here.
 *
 * NO LANGUAGE-WIDE SWEEP. `POST config/traducoes/traduzir_faltantes/{idioma}`
 * carries its only argument in the PATH and therefore has no InputContract, and
 * every write from a composable must name one (QA rule
 * `composable-contract-options`). The page-scoped `retraduzir()` covers the same
 * ground for the page the panel is showing; the sweep comes back the day the
 * PHP side declares `Traducoes_TraduzirFaltantesInput`.
 *
 * PAGE-SCOPED BY DESIGN. `lista` with no `pagina` makes the proxy read the
 * WHOLE upstream database (three multi-megabyte calls), so this composable
 * never fires a page-less list: `listar()` refuses without a page and the UI
 * always has one selected.
 *
 * BLANK TEXT IS A REFUSAL, NOT A CLEAR. Upstream writes with GORM's
 * `Updates(struct)`, which skips zero values, so an empty text is a silent
 * no-op that still answers success. The proxy refuses it with HTTP 422 and the
 * code `texto_vazio_nao_limpa_traducao`; `salvar()` re-throws that code
 * verbatim so the form can render it inline instead of toasting a stack trace.
 *
 * Module-scope singleton, like the other admin composables. Call `_reset()` in
 * afterEach for test isolation.
 */

/** The refusal code the proxy answers when `texto` is blank. */
export const TEXTO_VAZIO = 'texto_vazio_nao_limpa_traducao'

/** Default page size of the list — the proxy pages server-side. */
export const LIMITE_PADRAO = 50

export type BuscaModo = 'padrao' | 'traducao'

export interface ListarParams {
  pagina: string
  idioma?: string
  q?: string
  faltantes?: boolean
  limite?: number
  offset?: number
}

export interface BuscarParams {
  q: string
  modo?: BuscaModo
  idioma?: string
  pagina?: string
  limite?: number
}

// ── Module-scope singleton state ────────────────────────────────────────────

const idiomas = ref<Traducoes_IdiomaRow[]>([])
const paginas = ref<Traducoes_PaginaRow[]>([])
const itens = ref<Traducoes_ItemRow[]>([])
const total = ref<number>(0)
/** True when a search was truncated to `limite` — only `buscar()` sets it. */
const mais = ref<boolean>(false)
const loading = ref<boolean>(false)
const error = ref<string | null>(null)

function proxyUrl(path: string): string {
  return `${config.api.sistemaHost}/config/traducoes/${path}`
}

/**
 * The message to show for a failed call.
 *
 * A proxy refusal arrives as a REAL HTTP status (`MY_Controller::refuse()` sets
 * one), so httpService throws an HttpError whose `.data` is the envelope. The
 * envelope's `error` is the code the caller must be able to match on
 * (`texto_vazio_nao_limpa_traducao`, `pagina_inexistente`), so it wins over the
 * generic `HTTP 422: …` message fetch produced.
 */
function mensagemDeErro(err: unknown): string {
  const envelope = (err as { data?: { error?: string | null } })?.data
  const codigo = envelope?.error
  if (typeof codigo === 'string' && codigo !== '') return codigo
  return err instanceof Error ? err.message : String(err)
}

function falha(metodo: string, err: unknown, { toast = true } = {}): never {
  const mensagem = mensagemDeErro(err)
  log('useTranslationsAdmin', metodo, 'Erro', mensagem)
  error.value = mensagem
  if (toast) {
    eventBus.emit('toast.show', { severity: 'error', summary: 'Erro', detail: mensagem })
  }
  throw new Error(mensagem)
}

function sucesso(detail: string): void {
  eventBus.emit('toast.show', { severity: 'success', summary: 'Sucesso', detail })
}

/** Replace the item with the same page+key, if it is on screen. */
function upsertItem(item: Traducoes_ItemRow | undefined): void {
  if (!item) return
  const idx = itens.value.findIndex((i) => i.pagina === item.pagina && i.chave === item.chave)
  if (idx === -1) return
  itens.value = [...itens.value.slice(0, idx), item, ...itens.value.slice(idx + 1)]
}

export function useTranslationsAdmin() {
  /** GET config/traducoes/idiomas + paginas — the two selectors of the panel. */
  async function carregarCatalogo(): Promise<void> {
    loading.value = true
    error.value = null
    try {
      const [idiomasRaw, paginasRaw] = await Promise.all([
        httpService.get(proxyUrl('idiomas'), { responseContract: 'Traducoes_IdiomasResponse' }),
        httpService.get(proxyUrl('paginas'), { responseContract: 'Traducoes_PaginasResponse' }),
      ])
      idiomas.value = (idiomasRaw as { dados: Traducoes_IdiomasResponseDados }).dados.idiomas
      paginas.value = (paginasRaw as { dados: Traducoes_PaginasResponseDados }).dados.paginas
      log('useTranslationsAdmin', 'carregarCatalogo', 'Catálogo carregado', {
        idiomas: idiomas.value.length,
        paginas: paginas.value.length,
      })
    } catch (err) {
      falha('carregarCatalogo', err)
    } finally {
      loading.value = false
    }
  }

  /**
   * GET config/traducoes/lista — the table body.
   * `pagina` is mandatory here even though the contract allows it empty: see
   * the page-scoped note at the top of the file.
   */
  async function listar(params: ListarParams): Promise<Traducoes_ItemRow[]> {
    if (!params.pagina) {
      itens.value = []
      total.value = 0
      mais.value = false
      log('useTranslationsAdmin', 'listar', 'Sem página selecionada — nada a listar')
      return []
    }

    loading.value = true
    error.value = null
    try {
      const raw = await httpService.get(proxyUrl('lista'), {
        params: {
          pagina: params.pagina,
          idioma: params.idioma || '',
          q: params.q || '',
          faltantes: params.faltantes ? '1' : '0',
          limite: params.limite ?? LIMITE_PADRAO,
          offset: params.offset ?? 0,
        },
        responseContract: 'Traducoes_ListaResponse',
      })
      const dados = (raw as { dados: Traducoes_ListaResponseDados }).dados
      itens.value = dados.itens
      total.value = dados.total
      mais.value = false
      return itens.value
    } catch (err) {
      falha('listar', err)
    } finally {
      loading.value = false
    }
  }

  /**
   * GET config/traducoes/busca — `modo=padrao` matches the key (the pt-BR text
   * in the code), `modo=traducao` matches the text rendered in `idioma`.
   */
  async function buscar(params: BuscarParams): Promise<Traducoes_ItemRow[]> {
    loading.value = true
    error.value = null
    try {
      const raw = await httpService.get(proxyUrl('busca'), {
        params: {
          q: params.q,
          modo: params.modo ?? 'padrao',
          idioma: params.idioma || '',
          pagina: params.pagina || '',
          limite: params.limite ?? LIMITE_PADRAO,
        },
        responseContract: 'Traducoes_BuscaResponse',
      })
      const dados = (raw as { dados: Traducoes_BuscaResponseDados }).dados
      itens.value = dados.itens
      total.value = dados.total
      mais.value = dados.mais
      return itens.value
    } catch (err) {
      falha('buscar', err)
    } finally {
      loading.value = false
    }
  }

  /** GET config/traducoes/chave — one key in every language. */
  async function lerChave(pagina: string, chave: string): Promise<Traducoes_ItemRow | undefined> {
    error.value = null
    try {
      const raw = await httpService.get(proxyUrl('chave'), {
        params: { pagina, chave },
        responseContract: 'Traducoes_ChaveResponse',
      })
      return (raw as { dados: Traducoes_ChaveResponseDados }).dados.item
    } catch (err) {
      falha('lerChave', err)
    }
  }

  /**
   * POST config/traducoes/salvar — one key, one language.
   *
   * NO TOAST on failure: the only failure a translator can act on is
   * `texto_vazio_nao_limpa_traducao`, which the form renders inline next to the
   * field. The code is re-thrown so the caller can match on it.
   */
  async function salvar(input: Traducoes_SalvarInput): Promise<Traducoes_ItemRow | undefined> {
    error.value = null
    try {
      const raw = await httpService.post(proxyUrl('salvar'), input, {
        inputContract: 'Traducoes_SalvarInput',
        responseContract: 'Traducoes_ChaveResponse',
        skipErrorDisplay: true,
      })
      const resp = raw as {
        success?: boolean
        dados: Traducoes_ChaveResponseDados
        error?: string | null
      }
      if (resp.success === false) {
        throw new Error(resp.error ?? 'traducao_salvar_falhou')
      }
      const item = resp.dados.item
      upsertItem(item)
      sucesso('Tradução atualizada')
      return item
    } catch (err) {
      falha('salvar', err, { toast: false })
    }
  }

  /**
   * POST config/traducoes/chave — register a key on an existing page.
   * Idempotent upstream, so it is also the remedy for a key whose machine
   * translation never ran. `traduzido_automaticamente: false` means the key
   * exists but nothing translated it — say so instead of claiming success.
   */
  async function criarChave(
    input: Traducoes_CriarChaveInput,
  ): Promise<Traducoes_CriarChaveResponseDados | undefined> {
    error.value = null
    try {
      const raw = await httpService.post(proxyUrl('chave'), input, {
        inputContract: 'Traducoes_CriarChaveInput',
        responseContract: 'Traducoes_CriarChaveResponse',
        skipErrorDisplay: true,
      })
      const resp = raw as {
        success?: boolean
        dados: Traducoes_CriarChaveResponseDados
        error?: string | null
      }
      if (resp.success === false) {
        throw new Error(resp.error ?? 'traducao_criar_chave_falhou')
      }
      sucesso(
        resp.dados.traduzido_automaticamente
          ? 'Chave criada e traduzida'
          : 'Chave criada, mas nenhuma tradução automática foi gerada',
      )
      return resp.dados
    } catch (err) {
      falha('criarChave', err, { toast: false })
    }
  }

  /**
   * POST config/traducoes/retraduzir — one key, or (no `chave`) the WHOLE page,
   * which is one machine-translation call per key per language. The caller must
   * confirm the page-wide branch with the user first.
   */
  async function retraduzir(
    input: Traducoes_RetraduzirInput,
  ): Promise<Traducoes_RetraduzirResponseDados | undefined> {
    loading.value = true
    error.value = null
    try {
      const raw = await httpService.post(proxyUrl('retraduzir'), input, {
        inputContract: 'Traducoes_RetraduzirInput',
        responseContract: 'Traducoes_RetraduzirResponse',
        skipErrorDisplay: true,
      })
      const resp = raw as {
        success?: boolean
        dados: Traducoes_RetraduzirResponseDados
        error?: string | null
      }
      if (resp.success === false) {
        throw new Error(resp.error ?? 'traducao_retraduzir_falhou')
      }
      sucesso(`Retradução disparada para ${resp.dados.linhas} chave(s)`)
      return resp.dados
    } catch (err) {
      falha('retraduzir', err)
    } finally {
      loading.value = false
    }
  }

  /** Test isolation only — resets the module-scope singleton state. */
  function _reset(): void {
    idiomas.value = []
    paginas.value = []
    itens.value = []
    total.value = 0
    mais.value = false
    loading.value = false
    error.value = null
  }

  return {
    // State
    idiomas,
    paginas,
    itens,
    total,
    mais,
    loading,
    error,

    // Methods
    carregarCatalogo,
    listar,
    buscar,
    lerChave,
    salvar,
    criarChave,
    retraduzir,
    _reset,
  }
}
